import axios from 'axios';
import { ENV_CONFIG_PC_KEY, TOKEN_PC_KEY } from './constants'

import { msgLoading, msgError, msgClear } from './modal';

const OSS_CONFIG_URL = process.env.VUE_APP_OSS_CONFIG_URL;

let LOCKED_BUSINESS_API_URL = '';
let cachedApiUrls = [];
let currentPointerIndex = 0;
let detectionPromise = null;

// 并发中的显式 loading 请求数。默认请求不盖全屏遮罩。
// 需要遮罩时在调用处传 loading: true，例如 request({ url, loading: true })。
let loadingCount = 0;
let loadingDelayTimer = null;
const LOADING_SHOW_DELAY = 300;

function showGlobalLoading() {
  loadingCount++;
  if (loadingCount !== 1 || loadingDelayTimer) return;
  loadingDelayTimer = setTimeout(() => {
    loadingDelayTimer = null;
    if (loadingCount > 0) {
      msgLoading('请稍后');
    }
  }, LOADING_SHOW_DELAY);
}

function closeGlobalLoading(config) {
  if (!config || !config._loadingArmed || config._loadingClosed) return;
  config._loadingClosed = true;
  if (loadingCount <= 0) return;
  loadingCount--;
  if (loadingCount > 0) return;
  if (loadingDelayTimer) {
    clearTimeout(loadingDelayTimer);
    loadingDelayTimer = null;
    return;
  }
  msgClear();
}

const service = axios.create({
  timeout: 1000 * 10, // 10秒超时
});

// 监听来自 ConfigLoader 的配置热更新通知，物理粉碎当前的路由锁，强制下次请求重新测速
window.addEventListener('global_env_config_updated', (event) => {
  const newConfig = event.detail;
  LOCKED_BUSINESS_API_URL = '';
  cachedApiUrls = newConfig.BUSINESS_API_URLS || [];
  currentPointerIndex = 0;
});

/**
 * 连通性快速探测与最优路由锁定
 */
function bootstrapDomainDetection() {
  if (LOCKED_BUSINESS_API_URL) {
    return Promise.resolve(LOCKED_BUSINESS_API_URL);
  }

  if (detectionPromise) {
    return detectionPromise;
  }

  detectionPromise = (async () => {
    try {
      let cachedConfig = localStorage.getItem(ENV_CONFIG_PC_KEY);

      // 如果此时 ConfigLoader 还没下载完（极端冷启动场景），request.js 帮它兜底下载一次
      if (!cachedConfig) {
        const res = await axios.get(`${OSS_CONFIG_URL}?t=${new Date().getTime()}`);
        cachedConfig = JSON.stringify(res.data);
        localStorage.setItem(ENV_CONFIG_PC_KEY, cachedConfig);
      }

      const configObj = JSON.parse(cachedConfig);
      cachedApiUrls = configObj.BUSINESS_API_URLS || [];

      if (cachedApiUrls.length === 0) throw new Error('No available URLs');

      // 纯粹专注于对现有配置的测速拨测
      LOCKED_BUSINESS_API_URL = await Promise.any(
        cachedApiUrls.map(url =>
          axios.get(`${url}/api/ping`, { timeout: 2000 }).then(() => url)
        )
      );

      currentPointerIndex = cachedApiUrls.indexOf(LOCKED_BUSINESS_API_URL);
      return LOCKED_BUSINESS_API_URL;

    } catch (error) {
      // 测速全挂，说明本地缓存可能全是僵尸网关，清空它，逼迫下次刷新时 ConfigLoader 重新抓取
      localStorage.removeItem(ENV_CONFIG_PC_KEY);
      throw error;
    } finally {
      detectionPromise = null;
    }
  })();

  return detectionPromise;
}

/**
 * 运行中突发性断连节点平滑横跳方法
 */
function rotateToNextBackupDomain() {
  if (cachedApiUrls.length <= 1) return;
  currentPointerIndex++;
  if (currentPointerIndex >= cachedApiUrls.length) {
    currentPointerIndex = 0;
  }
  LOCKED_BUSINESS_API_URL = cachedApiUrls[currentPointerIndex];
}

/**
 * 当网关全挂或突发断连时，立即线上拉取最新 JSON 强制覆盖
 */
async function emergencyRefreshConfig() {
  try {
    const res = await axios.get(`${OSS_CONFIG_URL}?t=${new Date().getTime()}`);
    const serverConfig = res.data || {};

    if (serverConfig.BUSINESS_API_URLS && serverConfig.BUSINESS_API_URLS.length > 0) {
      // 1. 物理覆盖本地磁盘
      localStorage.setItem(ENV_CONFIG_PC_KEY, JSON.stringify(serverConfig));

      // 2. 内存变量立刻同步
      cachedApiUrls = serverConfig.BUSINESS_API_URLS;
      // 默认直接锁定新配置里的第一个主网关（或者你也可以在这里重新走一遍 bootstrapDomainDetection 测速）
      LOCKED_BUSINESS_API_URL = cachedApiUrls[0];
      currentPointerIndex = 0;

      // 3. 通知其他模块（如 WebSocket 长连接）网关变了
      window.dispatchEvent(new CustomEvent('global_env_config_updated', { detail: serverConfig }));
      return true;
    }
    return false;
  } catch (err) {
    console.error("[Config Center] 紧急自愈拉取 OSS 失败，连 OSS 根源也断连了", err);
    return false;
  }
}

// 请求拦截器
service.interceptors.request.use(
  async (config) => {
    config._loadingClosed = false;
    config._loadingArmed = config.loading === true;
    if (config._loadingArmed) {
      showGlobalLoading();
    }

    try {
      config.baseURL = await bootstrapDomainDetection();
      const token = localStorage.getItem(TOKEN_PC_KEY);
      if (token) {
        config.headers['Authorization'] = token;
      }
      return config;
    } catch (error) {
      closeGlobalLoading(config);
      window.dispatchEvent(new CustomEvent('global_request_failed_unlock'));
      msgError('请求失败，请检查网络状况');
      return new Promise(() => {}); // 强行熔断
    }
  },
  (error) => {
    closeGlobalLoading(error.config);
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (response) => {
    closeGlobalLoading(response.config);

    const res = response.data;
    if (res.code !== 200) {
      window.dispatchEvent(new CustomEvent('global_request_failed_unlock'));
      msgError(res.msg || '系统异常，请稍后再试');
      return new Promise(() => {}); // 熔断中断
    }
    return res;
  },
  async (error) => {
    const config = error.config;

    // 401 登录会话失效处理
    if (error.response && error.response.status === 401) {
      closeGlobalLoading(config);
      window.dispatchEvent(new CustomEvent('global_request_failed_unlock'));
      localStorage.clear()

      msgError('您的登录信息已过期，请重新登录');

      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
      return new Promise(() => {});
    }

    // 网络断连、网关变更、超时等突发性错误的无缝自愈
    // 如果没有 config，或者该请求已经标记为了“自愈重试请求”，说明连新网关也挂了，直接走终极报错
    if (!config || config._isIsolateRetried) {
      closeGlobalLoading(config);
      window.dispatchEvent(new CustomEvent('global_request_failed_unlock'));
      msgError('网络请求失败，请检查网络状况');
      return new Promise(() => {});
    }

    // 判定为网络异常（比如没有 response，或者状态码是 502/503/504/404 等网关崩溃表现）
    const hasResponse = !!error.response;
    const status = hasResponse ? error.response.status : null;
    const isNetworkError = !hasResponse || (status >= 500 || status === 404 || status === 405);

    if (isNetworkError) {

      // 核心拦截：立刻去线上捞取最新的配置 JSON
      const refreshSuccess = await emergencyRefreshConfig();

      if (refreshSuccess) {

        // 打上标记，防止死循环重试
        config._isIsolateRetried = true;

        // 如果原 url 是带域名的绝对路径，剥离它，强行替换成最新的 LOCKED_BUSINESS_API_URL
        if (config.url.startsWith('http://') || config.url.startsWith('https://')) {
          const urlObj = new URL(config.url);
          config.url = urlObj.pathname + urlObj.search;
        }
        config.baseURL = LOCKED_BUSINESS_API_URL;

        // 平衡 loading 计数器
        closeGlobalLoading(config);

        // 核心：原地复活！重新用最新的配置再次发起这次 axios 请求，并把结果返回给外层 await
        return service(config);
      }
    }

    // 3. 兜底逻辑：如果不是网络错误，或者 OSS 拉取失败，走原有的单网关轮询重试（可选保留，作为次级防御）
    if (!config.retryCount) config.retryCount = 0;
    if (config.retryCount < 2 && cachedApiUrls.length > 1) {
      config.retryCount++;

      if (config.url.startsWith('http://') || config.url.startsWith('https://')) {
        const urlObj = new URL(config.url);
        config.url = urlObj.pathname + urlObj.search;
      }

      rotateToNextBackupDomain();
      config.baseURL = LOCKED_BUSINESS_API_URL;

      // 重试会重新进入请求拦截器导致计数器递增，此处先递减一次保持平衡
      closeGlobalLoading(config);

      return new Promise(resolve => setTimeout(() => resolve(service(config)), 150));
    }

    // 通道全挂，全局终极告警
    closeGlobalLoading(config); // 彻底挂掉后，清除 Loading
    window.dispatchEvent(new CustomEvent('global_request_failed_unlock'));
    msgError('请求失败，请检查网络状况');
    return new Promise(() => {});
  }
);

export default service;
